import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/site";
import { PROCESS_STEPS } from "@/lib/siteContent";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export function Intro() {
  return (
    <section data-testid="home-intro" className="px-6 sm:px-10 lg:px-16 py-28 md:py-40 border-b border-white/10 bg-[#0B0C0E]">
      <Reveal className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8"><h2 className="font-display text-5xl sm:text-6xl lg:text-7xl max-w-5xl leading-[.94] text-white">Media made for modern real estate.</h2></div>
        <div className="lg:col-span-3 lg:col-start-10 lg:pt-5"><p className="text-sm md:text-base text-zinc-400 leading-relaxed">Premium photography, film and digital media designed to give every property an atmosphere buyers remember.</p><div className="mt-8 h-px w-14 bg-[#E5B869]" /></div>
      </Reveal>
    </section>
  );
}

export function FeaturedFilm({ settings }) {
  const [open, setOpen] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setShouldLoad(true); observer.disconnect(); }
    }, { rootMargin: "240px 0px" });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  return (
    <section ref={sectionRef} data-testid="featured-film" className="relative h-[82svh] min-h-[560px] overflow-hidden bg-[#0B0C0E] text-white">
      {settings.featured_film_poster && <img src={settings.featured_film_poster} alt="" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${shouldLoad ? "opacity-0" : "opacity-100"}`} />}
      {shouldLoad && settings.featured_film_url && <video src={settings.featured_film_url} poster={settings.featured_film_poster} autoPlay muted loop playsInline preload="metadata"
        className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/20 to-black/20" />
      <div className="relative h-full flex flex-col justify-end px-6 sm:px-10 lg:px-16 pb-16 md:pb-20">
        <Reveal>
          <div className="eyebrow-light">Film</div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 text-white leading-none">Built to be seen.</h2>
          <button data-testid="film-watch-btn" onClick={() => setOpen(true)} className="btn-outline-light mt-10">Watch</button>
        </Reveal>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl p-0 bg-black border-0 rounded-none">
          <DialogTitle className="sr-only">Featured Mandalay film</DialogTitle><DialogDescription className="sr-only">Watch Mandalay Real Estate Media’s featured film.</DialogDescription>
          {open && <video data-testid="film-player" src={settings.featured_film_url} controls autoPlay className="w-full aspect-video" />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function BrandStatement() {
  return (
    <section data-testid="brand-statement" className="px-6 sm:px-10 lg:px-16 py-32 md:py-48 bg-[#121418]">
      <Reveal>
        <div className="eyebrow mb-8">The Mandalay standard</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[.9] text-white">More than listing photography.</h2>
        <p className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[.9] text-[#C8B9A6]/65 mt-4">Visual media built for real estate.</p>
      </Reveal>
    </section>
  );
}

export function Process() {
  return (
    <section data-testid="home-process" className="px-6 sm:px-10 lg:px-16 py-24 md:py-36 border-t border-white/10 bg-[#0B0C0E]">
      <Reveal><div className="eyebrow">Simple from start to finish</div></Reveal>
      <div className="grid md:grid-cols-3 gap-10 md:gap-8 mt-16">
        {PROCESS_STEPS.map(([n, t, d], i) => (
          <Reveal key={n} delay={i * 0.1} className="border-t border-white/30 pt-6">
            <div className="text-[10px] tracking-[0.2em] text-[#E5B869] font-mono">{n}</div>
            <h3 className="font-display text-4xl md:text-5xl mt-6 text-white">{t}</h3>
            <p className="text-zinc-400 text-sm mt-4 leading-relaxed max-w-xs">{d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function PortalPreview({ settings }) {
  return (
    <section data-testid="portal-preview" className="px-6 sm:px-10 lg:px-16 py-24 md:py-36 border-t border-white/10 bg-[#121418]">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <div className="eyebrow">Mandalay Client Portal</div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[.94] text-white">Everything for your listing. One place.</h2>
          <p className="text-zinc-400 mt-8 max-w-md leading-relaxed">Book shoots, review photos, request edits and download your media from one simple dashboard.</p>
          <Link to="/login" data-testid="portal-login-btn" className="btn-outline mt-10">Client Login</Link>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.15}>
          <PortalMockup image={settings.portal_image_url} />
        </Reveal>
      </div>
    </section>
  );
}

function PortalMockup({ image }) {
  const rows = [["Southampton, NY", "Delivered", "48 files"], ["Tribeca, NY", "In production", "—"], ["Houston, TX", "Scheduled", "Jun 28"]];
  return (
    <div className="bg-[#0B0C0E] border border-white/15 shadow-[0_30px_80px_-40px_rgba(0,0,0,.8)]">
      <div className="h-10 border-b border-white/10 flex items-center px-4 gap-1.5">
        {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full bg-white/20" />)}
        <span className="ml-4 text-[10px] uppercase tracking-[0.25em] text-white/40">Dashboard</span>
      </div>
      <div className="grid grid-cols-12">
        <div className="col-span-5 aspect-[4/5] overflow-hidden"><img src={image} alt="" className="w-full h-full object-cover" /></div>
        <div className="col-span-7 p-6 md:p-8">
          <div className="eyebrow">Your listings</div>
          <div className="mt-6 divide-y divide-white/10">
            {rows.map(([a, s, f]) => (
              <div key={a} className="py-4 flex items-center justify-between gap-4">
                <div><div className="text-sm font-medium text-white">{a}</div><div className="text-[10px] uppercase tracking-[0.15em] text-white/40 mt-1">{s}</div></div>
                <div className="text-xs text-white/55">{f}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 inline-flex h-9 px-5 items-center bg-[#E5B869] text-[#0B0C0E] text-[10px] uppercase tracking-[0.2em]">Download all</div>
        </div>
      </div>
    </div>
  );
}

export function Markets({ markets }) {
  return (
    <section data-testid="home-markets" className="flex min-h-[54svh] items-center border-t border-white/10 bg-[#0B0C0E] px-6 py-24 sm:px-10 md:py-32 lg:px-16">
      <div className="mx-auto w-full max-w-[1440px] text-center"><Reveal><div className="flex items-center justify-center gap-4"><span aria-hidden="true" className="h-px w-10 bg-[#E5B869]/70" /><div className="eyebrow">Serving</div><span aria-hidden="true" className="h-px w-10 bg-[#E5B869]/70" /></div></Reveal>
      <div className="mt-9 space-y-2 sm:space-y-3">
        {markets.map((m, i) => <Reveal key={m.id} delay={i * 0.08}><div data-testid={`market-${m.id}`} className="font-display text-6xl uppercase leading-[.9] text-white sm:text-7xl lg:text-8xl">{m.name}</div></Reveal>)}
      </div>
      </div>
    </section>
  );
}
